import { Alert, Box, Button, Pagination, Typography } from '@mui/material';
import {
  LoadingEvent,
  useDataProvider,
  useMasterData,
} from '@ui-framework/index';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { forwardRef, useEffect, useState } from 'react';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { useDropzone } from 'react-dropzone';
import { fetchAttachmentForNote } from './lib/lib';
import FileDetail from './FileDetail';
import getSupportedFileTypes from './lib/getSupportedFileTypes';
import IconLibrary from '../../IconLibrary';
import FileUpload from './FileUpload';

const NoteAttachmentList = forwardRef((props, ref) => {
  const limit = 5;

  const UNSUPPORTED_FILE_ERROR = {
    name: '',
    message: '',
  };

  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const { get, getApiUrl, control } = useDataProvider(provider);
  const {
    getValues,
    setError: setFormError,
    formState: { submitCount, isDirty },
  } = useFormContext();
  const [unSupportedFileError, setUnSupportedFileError] = useState(
    UNSUPPORTED_FILE_ERROR,
  );
  const { fields, append, remove } = useFieldArray({
    control,
    name: props?.field?.name,
  });

  const masterData = useMasterData();
  const currentValues = getValues();

  const [fileApiResponse, setFileApiResponse] = useState(null);
  const [loading, setLoading] = useState(true);

  const canEdit = props?.field?.permission === 'WRITE';

  const commonParams = {
    getApiUrl,
    get,
  };

  const { open, acceptedFiles, getInputProps } = useDropzone({
    noClick: true,
    noKeyboard: true,
    accept: getSupportedFileTypes(masterData.attachmentFileExtensions),
    multiple: false,
    onDropAccepted: () => {
      setUnSupportedFileError(UNSUPPORTED_FILE_ERROR);
    },
    onDropRejected: fileRejections => {
      const rejection = fileRejections[0];
      if (!rejection) {
        return;
      }

      rejection.errors.forEach(error => {
        setUnSupportedFileError({
          name: rejection.file.name,
          message: error?.message,
        });
      });
    },
  });

  const initialize = async (page = 1) => {
    setLoading(true);
    try {
      const fetchAttachmentsResponse = await fetchAttachmentForNote({
        params: commonParams,
        serviceJobId: currentValues?.serviceJobId,
        id: currentValues?.noteId,
        page,
        limit,
      });
      if (fetchAttachmentsResponse?.ok) {
        setFileApiResponse(fetchAttachmentsResponse?.data);
      } else if (Array.isArray(fetchAttachmentsResponse?.errors)) {
        fetchAttachmentsResponse?.errors?.forEach(error => {
          setFormError('', {
            type: 'server',
            message: error?.message,
          });
        });
      } else {
        setFormError('', {
          type: 'server',
          message: 'Could not load the attachments',
        });
      }
    } catch (err) {
      setFormError('', {
        type: 'server',
        message: err?.message,
      });
    }
    setLoading(false);
  };

  useEffect(() => {
    if (currentValues?.noteId) {
      initialize();
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (acceptedFiles.length > 0) {
      acceptedFiles.forEach(file => {
        append({ file, attachmentType: '', description: '' });
      });
    }
  }, [acceptedFiles, append]);

  useEffect(() => {
    if (submitCount > 0 || !isDirty) {
      setUnSupportedFileError(UNSUPPORTED_FILE_ERROR);
    }
  }, [submitCount, isDirty]);

  if (loading) {
    return <LoadingEvent>Loading Attachments...</LoadingEvent>;
  }

  const pageCount = fileApiResponse?.total
    ? Math.ceil(fileApiResponse.total / limit)
    : 0;

  const hasAttachments =
    Array.isArray(fileApiResponse?.data) && fileApiResponse?.data?.length > 0;

  return (
    <Box ref={ref}>
      <input {...getInputProps()} />
      <Box>
        {canEdit && (hasAttachments || fields.length > 0) && (
          <Grid2 container sx={{ mb: 1 }}>
            <Grid2 xs={6}>
              {fields.length > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Files to be uploaded :
                </Typography>
              )}
            </Grid2>
            <Grid2 xs={6} sx={{ textAlign: 'right' }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  justifyContent: 'end',
                }}>
                {unSupportedFileError.message && (
                  <Alert onClose={() => {}} sx={{ py: 0.2 }} severity="error">
                    Unsupported File Format
                  </Alert>
                )}
                <Button
                  size="small"
                  variant="contained"
                  startIcon={<IconLibrary.Upload />}
                  onClick={() => {
                    open();
                  }}>
                  Add Attachments
                </Button>
              </Box>
            </Grid2>
          </Grid2>
        )}
        {fields.length > 0 && (
          <Box mb={3}>
            {fields.map((field, index) => (
              <FileUpload
                key={field.id}
                file={field.file}
                onRemove={() => remove(index)}
                control={control}
                fieldName={props?.field?.name}
                index={index}
              />
            ))}
          </Box>
        )}
        {Array.isArray(fileApiResponse?.data) &&
          fileApiResponse?.data?.length === 0 &&
          !canEdit && <Typography>No attachments to display</Typography>}
      </Box>
      {Array.isArray(fileApiResponse?.data) &&
      fileApiResponse?.data?.length > 0 ? (
        <Box>
          <Box>
            {fileApiResponse?.data?.map(file => (
              <FileDetail
                key={file?.id}
                file={file}
                noteId={currentValues?.noteId}
                serviceJobId={currentValues?.serviceJobId}
                reloadAttachmentList={() => {
                  initialize();
                }}
              />
            ))}
          </Box>
          <Box sx={{ justifyItems: 'center' }}>
            {pageCount > 1 && (
              <Pagination
                page={fileApiResponse?.page || 1}
                onChange={async (event, value) => {
                  await initialize(value);
                }}
                sx={{
                  mt: 1,
                  mr: 1,
                  ml: 1,
                  justifyContent: 'flex-end',
                  display: 'flex',
                }}
                count={pageCount}
                size="small"
              />
            )}
          </Box>
        </Box>
      ) : (
        <Box>
          {canEdit && (!hasAttachments || fields.length > 0) ? (
            <Box sx={{ mb: 3 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  justifyContent: 'start',
                }}>
                <Typography variant="body">
                  No attachments to display
                </Typography>

                <Button
                  sx={{ ml: 2 }}
                  size="small"
                  variant="contained"
                  startIcon={<IconLibrary.Upload />}
                  onClick={() => {
                    open();
                  }}>
                  Add Attachments
                </Button>
                {unSupportedFileError.message && (
                  <Alert onClose={() => {}} sx={{ py: 0.2 }} severity="error">
                    Unsupported File Format
                  </Alert>
                )}
              </Box>
            </Box>
          ) : null}
        </Box>
      )}
    </Box>
  );
});

export default NoteAttachmentList;
